/**
 * HRIS Recruitment API — TanStack Query hooks for /v4/job-candidates.
 *
 * API shape mirrors apps/rest_server/routes/v4/job-candidates.js.
 * Stages match the RecruitmentStage enum from the backend stage machine.
 * Optimistic updates on stage moves; 422 non-adjacent → revert + French toast.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RecruitmentStage =
  | 'nouveau'
  | 'preselection'
  | 'r1_culture_fit'
  | 'r2_competence_technique'
  | 'r3_passion'
  | 'recommandation_offre'
  | 'embauche';

export type CandidateSource = 'indeed' | 'website' | 'referral' | 'linkedin' | 'other';

export const STAGES: readonly RecruitmentStage[] = [
  'nouveau',
  'preselection',
  'r1_culture_fit',
  'r2_competence_technique',
  'r3_passion',
  'recommandation_offre',
  'embauche',
] as const;

export const STAGE_LABELS: Record<RecruitmentStage, string> = {
  nouveau: 'Nouveau',
  preselection: 'Présélection',
  r1_culture_fit: 'R1 – Culture Fit',
  r2_competence_technique: 'R2 – Compétence',
  r3_passion: 'R3 – Passion',
  recommandation_offre: 'Recommandation / Offre',
  embauche: 'Embauche',
};

export const SOURCE_LABELS: Record<CandidateSource, string> = {
  indeed: 'Indeed',
  website: 'Site web',
  referral: 'Référence',
  linkedin: 'LinkedIn',
  other: 'Autre',
};

export interface CandidateEvent {
  id: string;
  candidateId: string;
  fromStage: RecruitmentStage | null;
  toStage: RecruitmentStage;
  actorUserId: string | null;
  note: string | null;
  createdAt: string;
}

export interface Candidate {
  id: string;
  jobPostingId: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  cvUrl: string | null;
  cvPending: boolean;
  source: CandidateSource;
  stage: RecruitmentStage;
  score: string | null;
  notes: string | null;
  questionSetUrl: string | null;
  debriefUrl: string | null;
  appliedAt: string;
  reappliedAt: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  events?: CandidateEvent[];
}

/** GET /v4/job-candidates?jobPostingId= returns candidates grouped by stage */
export type CandidatesByStage = Record<RecruitmentStage, Candidate[]>;

export interface CandidatesListResponse {
  candidates: CandidatesByStage;
  total: number;
}

export interface CandidateFilters {
  jobPostingId?: string;
  source?: CandidateSource | '';
  showArchived?: boolean;
}

export type UpdateCandidateInput = Partial<
  Pick<Candidate, 'stage' | 'notes' | 'score' | 'questionSetUrl' | 'debriefUrl' | 'archived'>
>;

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const candidateKeys = {
  all: ['job-candidates'] as const,
  lists: () => [...candidateKeys.all, 'list'] as const,
  list: (filters?: CandidateFilters) => [...candidateKeys.lists(), filters] as const,
  details: () => [...candidateKeys.all, 'detail'] as const,
  detail: (id: string) => [...candidateKeys.details(), id] as const,
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function fetchCandidates(
  filters: CandidateFilters = {}
): Promise<CandidatesListResponse> {
  const params: Record<string, string> = {};
  if (filters.jobPostingId) params.jobPostingId = filters.jobPostingId;
  if (filters.source) params.source = filters.source;
  if (filters.showArchived) params.showArchived = 'true';
  return api.get<CandidatesListResponse>('/v4/job-candidates', params);
}

async function fetchCandidate(id: string): Promise<Candidate> {
  return api.get<Candidate>(`/v4/job-candidates/${id}`);
}

async function patchCandidate(
  id: string,
  data: UpdateCandidateInput
): Promise<Candidate> {
  return api.patch<Candidate>(`/v4/job-candidates/${id}`, data);
}

// ---------------------------------------------------------------------------
// Error utility
// ---------------------------------------------------------------------------

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Une erreur inattendue est survenue';
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches all candidates for a job posting, grouped by stage.
 * When jobPostingId is empty/undefined the query is disabled.
 */
export function useCandidates(filters: CandidateFilters = {}) {
  return useQuery({
    queryKey: candidateKeys.list(filters),
    queryFn: () => fetchCandidates(filters),
    enabled: !!filters.jobPostingId,
  });
}

/**
 * Fetches a single candidate (with event history appended by the backend).
 */
export function useCandidate(id: string) {
  return useQuery({
    queryKey: candidateKeys.detail(id),
    queryFn: () => fetchCandidate(id),
    enabled: !!id,
  });
}

/**
 * Moves a candidate to an adjacent stage (±1). Applies an optimistic update so
 * the card snaps immediately; on 422 (non-adjacent / terminal) reverts and
 * shows a French toast "Transition non permise".
 */
export function useMoveCandidate(filters: CandidateFilters = {}) {
  const queryClient = useQueryClient();
  const listKey = candidateKeys.list(filters);

  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: RecruitmentStage }) =>
      patchCandidate(id, { stage }),

    onMutate: async ({ id, stage }) => {
      // Cancel in-flight refetches so they don't overwrite the optimistic update.
      await queryClient.cancelQueries({ queryKey: listKey });

      const previous = queryClient.getQueryData<CandidatesListResponse>(listKey);

      if (previous) {
        // Build a new grouped structure with the card moved.
        const next: CandidatesListResponse = {
          total: previous.total,
          candidates: { ...previous.candidates } as CandidatesByStage,
        };
        // Find and remove the candidate from its current stage.
        let moved: Candidate | undefined;
        for (const s of STAGES) {
          const col = next.candidates[s] ?? [];
          const idx = col.findIndex((c) => c.id === id);
          if (idx !== -1) {
            moved = col[idx];
            next.candidates[s] = col.filter((c) => c.id !== id);
            break;
          }
        }
        if (moved) {
          next.candidates[stage] = [
            ...(next.candidates[stage] ?? []),
            { ...moved, stage },
          ];
        }
        queryClient.setQueryData(listKey, next);
      }

      return { previous };
    },

    onError: (error, _vars, context) => {
      // Revert on any error.
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
      if (error instanceof ApiError && error.status === 422) {
        toast.error('Transition non permise', {
          description: 'Vous ne pouvez déplacer un candidat que d\'une étape à la fois.',
        });
      } else {
        toast.error('Erreur lors du déplacement', {
          description: getErrorMessage(error),
        });
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}

/**
 * General-purpose update (notes autosave, score, archiving, etc.).
 */
export function useUpdateCandidate(filters?: CandidateFilters) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCandidateInput }) =>
      patchCandidate(id, data),

    onSuccess: (updated) => {
      // Update the detail cache.
      queryClient.setQueryData(candidateKeys.detail(updated.id), updated);
      // Invalidate list queries so counts refresh.
      queryClient.invalidateQueries({ queryKey: candidateKeys.lists() });
      // If we have filters context, update that specific list too.
      if (filters) {
        queryClient.invalidateQueries({ queryKey: candidateKeys.list(filters) });
      }
    },

    onError: (error) => {
      toast.error('Erreur lors de la mise à jour', {
        description: getErrorMessage(error),
      });
    },
  });
}
